import { RippCore } from "./v2/RippCore";
import { Play, Square, Power, RotateCcw, Activity, Zap, FlaskConical, LogOut, Users, Brain, Newspaper, Waves } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { money, signedMoney, pct, pnlColor } from "../lib/format";
import { HolderBadge } from "./HolderBadge";

const coreModeOf = (state, connected) => {
  if (!connected) return "offline";
  if (state?.config?.kill_switch || state?.config?.day_halted) return "risklock";
  const pos = state?.pme?.positions?.[0];
  if (pos?.scaleInEligible) return "scalein";
  if (pos?.tpStatus?.some((t) => t.status === "APPROACHING")) return "target";
  if (pos) return "position";
  if ((state?.pending || []).length) return "executing";
  if ((state?.ai?.running || []).length) return "analyzing";
  return state?.config?.running ? "scanning" : "idle";
};

export const TopBar = ({ state, connected, onStart, onStop, onKill, onReset, onBacktest, onWaitlist, onForecast, onNews, onLogout }) => {
  const navigate = useNavigate();
  const acc = state?.account || {};
  const running = state?.config?.running;
  const halted = state?.day_halted;

  const Stat = ({ label, value, cls = "text-zinc-100" }) => (
    <div className="flex flex-col px-3 border-l border-zinc-800">
      <span className="text-[9.5px] uppercase tracking-wider text-zinc-500">{label}</span>
      <span className={`font-mono text-[13px] font-semibold ${cls}`}>{value}</span>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center justify-between border border-zinc-800 bg-[#09090b] min-h-14 py-1 gap-y-1 shrink-0">
      <div className="flex items-center gap-3 px-4 shrink-0">
        <div className="flex items-center gap-2">
          <Link to="/v2" title="Open RIPP 2.0 command center" data-testid="topbar-core" className="relative grid place-items-center h-8 w-8 -m-1">
            <RippCore mode={coreModeOf(state, connected)} agents={[]} size={40} compact />
          </Link>
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-bold tracking-tight text-zinc-100">RIPP <span className="text-[var(--r2-cyan)]">LABS</span></span>
            <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Smart Money Terminal</span>
          </div>
        </div>
        <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/25">
          {state?.config?.mode === "LIVE" ? "🔴 LIVE" : "PAPER"} · {state?.config?.env?.toUpperCase() || "DEMO"}
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-zinc-400">
          <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500 live-dot" : "bg-red-500"}`} />
          {connected ? "LIVE" : "RECONNECTING"}
        </span>
        <HolderBadge />
        {(() => {
          const srcs = (state?.market || []).map((m) => m.source);
          const broker = srcs.includes("BROKER");
          const market = srcs.includes("MARKET");
          const label = broker ? "● LIVE DATA" : market ? "● MARKET DATA" : "SIM DATA";
          const good = broker || market;
          return (
            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm border ${good ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-zinc-700/30 text-zinc-400 border-zinc-700"}`}>
              {label}
            </span>
          );
        })()}
        {halted && (
          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-red-500/15 text-red-400 border border-red-500/30">
            DAILY LOSS HALT
          </span>
        )}
        {(() => {
          const cfg = state?.config || {};
          const armed = !!cfg.trust_mode;
          const size = cfg.contract_mode || "MICRO";
          const micro = size === "MICRO";
          const ok = armed && micro;
          const label = !armed ? `Trust: OFF (${size})` : micro ? "Trust: ARMED (MICRO)" : `Trust: BLOCKED (${size})`;
          const cls = ok
            ? "bg-green-500/15 text-green-400 border-green-500/40"
            : armed
            ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
            : "bg-zinc-700/30 text-zinc-400 border-zinc-700";
          return (
            <span
              data-testid="trust-status-chip"
              title={
                ok
                  ? "Trust Mode is ARMED — micro-size entries auto-execute without confirmation."
                  : armed
                  ? `Trust Mode is on but contract size is ${size}. Auto-execution only runs on MICRO size.`
                  : "Trust Mode is OFF — signals require manual confirmation."
              }
              className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded-sm border ${cls}`}
            >
              <Zap size={10} className={ok ? "text-green-400" : armed ? "text-amber-400" : "text-zinc-500"} />
              {label}
            </span>
          );
        })()}
        {(() => {
          const sessions = state?.sessions || {};
          const keys = Object.keys(sessions);
          if (!keys.length) return null;
          const open = keys.filter((k) => sessions[k]?.open).length;
          const filterOn = state?.config?.session_filter;
          const anyOpen = open > 0;
          return (
            <span
              data-testid="session-badge"
              title={filterOn ? "Session filter ON" : "Session filter OFF"}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm border ${anyOpen ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-zinc-700/30 text-zinc-400 border-zinc-700"}`}
            >
              {anyOpen ? `● SESSION ${open}/${keys.length}` : "SESSION CLOSED"}{!filterOn && " · OFF"}
            </span>
          );
        })()}
        {state?.tradovate?.msg_stats && (
          (() => {
            const m = state.tradovate.msg_stats;
            const near = m.last_min >= m.cap_min * 0.7 || m.today >= m.cap_day * 0.7;
            return (
              <span
                data-testid="msg-rate-badge"
                title={`Order-message throttle — min ${m.min_gap_s}s gap. This minute: ${m.last_min}/${m.cap_min}. Today: ${m.today}/${m.cap_day}. Well below any exchange messaging limit.`}
                className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm border ${near ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-zinc-700/30 text-zinc-400 border-zinc-700"}`}
              >
                MSG {m.last_min}/{m.cap_min}·min · {m.today}/{m.cap_day}·day
              </span>
            );
          })()
        )}
      </div>

      <div className="flex flex-wrap items-center gap-y-1">
        <div className="hidden xl:flex items-center">
        <Stat label="Equity" value={money(acc.equity)} />
        <Stat label="Balance" value={money(acc.balance)} />
        <Stat label="Unrealized" value={signedMoney(acc.unrealized_pnl)} cls={pnlColor(acc.unrealized_pnl)} />
        <Stat label="Daily P&L" value={signedMoney(acc.daily_pnl)} cls={pnlColor(acc.daily_pnl)} />
        <Stat label="Max DD" value={pct(-(acc.max_drawdown || 0))} cls="text-red-400" />
        <Stat label="Win %" value={`${acc.winrate ?? 0}%`} cls="text-zinc-100" />
        {state?.vix?.price != null && (
          <Stat
            label="VIX"
            value={`${state.vix.price}${state.vix.change_1h != null ? ` (${state.vix.change_1h >= 0 ? "+" : ""}${state.vix.change_1h})` : ""}`}
            cls={state.vix.change_1h > 0.05 ? "text-red-400" : state.vix.change_1h < -0.05 ? "text-green-400" : "text-zinc-300"}
          />
        )}
        {state?.mag7?.bias && state?.mag7?.count > 0 && (
          <div className="flex flex-col px-3 border-l border-zinc-800" data-testid="mag7-stat" title={`Magnificent 7: ${(state.mag7.members||[]).map(m=>`${m.symbol} ${m.change_pct>=0?'+':''}${m.change_pct}%`).join('  ·  ')}`}>
            <span className="text-[9.5px] uppercase tracking-wider text-zinc-500">MAG 7</span>
            <span className={`font-mono text-[13px] font-semibold ${state.mag7.bias === "BULLISH" ? "text-green-400" : state.mag7.bias === "BEARISH" ? "text-red-400" : "text-amber-300"}`}>
              {state.mag7.bias === "BULLISH" ? "▲" : state.mag7.bias === "BEARISH" ? "▼" : "◆"} {state.mag7.up}/{state.mag7.count}
              {state.mag7.avg_change_pct != null && <span className="text-zinc-400 text-[11px]"> {state.mag7.avg_change_pct >= 0 ? "+" : ""}{state.mag7.avg_change_pct}%</span>}
            </span>
          </div>
        )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 px-4 border-l border-zinc-800">
          <button
            data-testid="open-engine-btn"
            onClick={() => navigate("/engine")}
            title="Liquid Intelligence Visual Engine"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm bg-[#38BDF8]/15 text-[#38BDF8] border border-[#38BDF8]/40 hover:bg-[#38BDF8]/25 transition-colors duration-150"
          >
            <Waves size={13} /> LIQUID ENGINE
          </button>
          <button
            data-testid="open-backtest-btn"
            onClick={onBacktest}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors duration-150"
          >
            <FlaskConical size={13} /> BACKTEST
          </button>
          <button
            data-testid="open-forecast-btn"
            onClick={onForecast}
            title="3-scenario forecast engine (measurable + scored)"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-700/40 hover:bg-cyan-500/25 transition-colors duration-150"
          >
            <Brain size={13} /> FORECAST
          </button>
          <button
            data-testid="open-news-btn"
            onClick={onNews}
            title="Live finance news"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-amber-300 hover:bg-zinc-700 transition-colors duration-150"
          >
            <Newspaper size={13} /> NEWS
          </button>
          <button
            data-testid="open-waitlist-btn"
            onClick={onWaitlist}
            title="View landing-page waitlist signups"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors duration-150"
          >
            <Users size={13} /> WAITLIST
          </button>
          {running ? (
            <button
              data-testid="stop-agent-btn"
              onClick={onStop}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700 transition-colors duration-150"
            >
              <Square size={13} /> STOP
            </button>
          ) : (
            <button
              data-testid="start-agent-btn"
              onClick={onStart}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-green-600 text-white hover:bg-green-500 transition-colors duration-150"
            >
              <Play size={13} /> START
            </button>
          )}
          <button
            data-testid="reset-agent-btn"
            onClick={onReset}
            title="Reset account & history"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors duration-150"
          >
            <RotateCcw size={13} />
          </button>
          <button
            data-testid="kill-switch"
            onClick={onKill}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm bg-red-600 text-white hover:bg-red-500 transition-colors duration-150 ring-1 ring-red-400/40"
          >
            <Power size={13} /> KILL SWITCH
          </button>
          {onLogout && (
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              title="Log out"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors duration-150"
            >
              <LogOut size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
